/**
 * GuestLinkStatePages
 * The pages a personal guest link can land on besides its content: invalid,
 * parked (expired, replaced, legacy), nothing delegated, and the loading
 * shell. Parked pages carry one self-service action behind a button — never
 * on page load, since mail link scanners prefetch URLs.
 * All render outside the app providers: no contexts, no user names.
 */
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Alert, Button, Card, Spinner } from 'react-bootstrap';
import { AlertCircle, CheckCircle2, Clock, Inbox, RefreshCw } from 'lucide-react';
import { guestApi } from '../../../api/guestApi';
import { guestLinkRefusalFromError, GuestLinkRefusalError } from '../../../api/guestLinkRefusal';
import { GuestLinkRefusal } from '../../../types/guestAccess';
import logoWhite from '../../../Images/logo_white.png';
import '../GuestTaskPage.css';

export const GuestShell: React.FC<{ className?: string; children: React.ReactNode }> = ({ className, children }) => (
    <div className={`guest-page ${className ?? ''}`.trim()}>
        <div className="guest-page__header">
            <img src={logoWhite} alt="Molnfastigheter" className="guest-page__header-logo" />
        </div>
        <div className="guest-page__centered">
            <Card className="text-center guest-link-state">
                <Card.Body>{children}</Card.Body>
            </Card>
        </div>
        <footer className="guest-page__footer">Powered by Molnfastigheter Sverige AB</footer>
    </div>
);

/** Missing or revoked (manual / user deleted) link. */
export const GuestInvalidLinkPage: React.FC = () => (
    <GuestShell className="guest-page--error">
        <AlertCircle size={48} className="text-danger mb-3" />
        <h4>Länken är inte giltig</h4>
        <p className="text-muted mb-0">
            Kontakta den som skickade länken om du behöver en ny.
        </p>
    </GuestShell>
);

/**
 * The link is fine but this read failed: a task that is no longer served
 * (cancelled, completed long ago, undelegated) or a plain error. Never the
 * "invalid link" page, which is reserved for the `invalid` refusal.
 */
export const GuestUnavailablePage: React.FC<{ title: string; text?: string; token?: string }> = ({ title, text, token }) => (
    <GuestShell className="guest-page--error">
        <AlertCircle size={48} className="text-secondary mb-3" />
        <h4>{title}</h4>
        {text && <p className="text-muted mb-0">{text}</p>}
        {token && (
            <Link to={`/guest/${token}`} className="btn btn-primary btn-lg w-100 mt-3">Alla mina uppgifter</Link>
        )}
    </GuestShell>
);

/**
 * A load that failed for another reason than the link or the delegation
 * (network, server error, rate limit): says so, instead of suggesting the
 * task was taken away, and offers a retry.
 */
export const GuestLoadErrorPage: React.FC<{ title: string; message: string }> = ({ title, message }) => (
    <GuestShell className="guest-page--error">
        <AlertCircle size={48} className="text-danger mb-3" />
        <h4>{title}</h4>
        <p className="text-muted mb-0">{message}</p>
        <Button variant="primary" size="lg" className="w-100 mt-3" onClick={() => window.location.reload()}>
            <RefreshCw size={18} className="me-2" />
            Försök igen
        </Button>
    </GuestShell>
);

/** The user has no delegations at all, so nothing is e-mailed. */
export const GuestNoDelegationsPage: React.FC = () => (
    <GuestShell className="guest-page--error">
        <Inbox size={48} className="text-secondary mb-3" />
        <h4>Du har inga aktiva uppgifter</h4>
        <p className="text-muted mb-0">
            När förvaltaren delegerar något nytt till dig får du ett e-postmeddelande med länken.
        </p>
    </GuestShell>
);

interface GuestSelfServicePageProps {
    token: string;
    icon: React.ReactNode;
    title: string;
    text: React.ReactNode;
    buttonLabel: string;
    /** Runs on click only; resolves to the text shown when it succeeded. */
    action: () => Promise<string>;
    /** The refusal this page already answers; the same refusal again is shown inline, not re-routed. */
    ownState?: GuestLinkRefusal;
    /** Shown under the success text, e.g. a link onwards. */
    successFooter?: React.ReactNode;
}

/**
 * One self-service page: a title, an explanation and a single button. A
 * refusal from the action re-routes to the matching parked page (an expired
 * link whose user has no delegations, a reported link that is already replaced).
 */
export const GuestSelfServicePage: React.FC<GuestSelfServicePageProps> = ({
    token, icon, title, text, buttonLabel, action, ownState, successFooter,
}) => {
    const [sending, setSending] = useState(false);
    const [done, setDone] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [refusal, setRefusal] = useState<GuestLinkRefusalError | null>(null);

    if (refusal) {
        return <GuestLinkRefusalPage token={token} refusal={refusal} />;
    }

    const run = async () => {
        setSending(true);
        setError(null);
        try {
            setDone(await action());
        } catch (err) {
            const refused = guestLinkRefusalFromError(err);
            if (refused && refused.linkState !== ownState) {
                setRefusal(refused);
            } else {
                setError(err instanceof Error && err.message ? err.message : 'Något gick fel. Försök igen.');
            }
        } finally {
            setSending(false);
        }
    };

    return (
        <GuestShell className="guest-page--error">
            {done ? (
                <>
                    <CheckCircle2 size={48} className="text-success mb-3" />
                    <h4>{title}</h4>
                    <p className="mb-0">{done}</p>
                    {successFooter && <div className="mt-3">{successFooter}</div>}
                </>
            ) : (
                <>
                    {icon}
                    <h4>{title}</h4>
                    <p className="text-muted">{text}</p>
                    {error && <Alert variant="danger" className="text-start">{error}</Alert>}
                    <Button variant="primary" size="lg" className="w-100" onClick={run} disabled={sending}>
                        {sending ? <Spinner animation="border" size="sm" /> : buttonLabel}
                    </Button>
                </>
            )}
        </GuestShell>
    );
};

const sentTo = (what: string) => ({ maskedEmail }: { maskedEmail: string }) => `${what} skickad till ${maskedEmail}`;

/**
 * Expiring link past its date: access has ended. The guest can only ask the
 * admins to extend it; an admin decides (a new link arrives by e-mail).
 */
export const GuestExpiredLinkPage: React.FC<{ token: string }> = ({ token }) => (
    <GuestSelfServicePage
        token={token}
        ownState="expired"
        icon={<Clock size={48} className="text-warning mb-3" />}
        title="Länken har gått ut"
        text="Åtkomsten via länken har upphört. Du kan be förvaltaren att förlänga den."
        buttonLabel="Be om förlängning"
        action={() => guestApi.requestExtension(token).then(({ alreadyRequested }) => (alreadyRequested
            ? 'Du har redan bett om förlängning. Du får en ny länk via e-post om förvaltaren förlänger din åtkomst.'
            : 'Förfrågan har skickats till förvaltaren. Du får en ny länk via e-post om din åtkomst förlängs.'))}
    />
);

/** Rotated or legacy link: the user's current link is e-mailed. */
export const GuestReplacedLinkPage: React.FC<{ token: string; legacy?: boolean }> = ({ token, legacy }) => (
    <GuestSelfServicePage
        token={token}
        ownState={legacy ? 'legacy' : 'replaced'}
        icon={<RefreshCw size={48} className="text-secondary mb-3" />}
        title={legacy ? 'Länkarna har bytts ut' : 'Den här länken är utbytt'}
        text={legacy
            ? 'Du har nu en personlig länk som öppnar alla dina uppgifter. Vi skickar den till din e-postadress.'
            : 'Länken har ersatts av en ny. Vi skickar din nuvarande länk till din e-postadress.'}
        buttonLabel="Skicka min nuvarande länk"
        action={() => guestApi.requestCurrentLink(token).then(sentTo('Länk'))}
    />
);

/** The parked or invalid page for a refusal from any guest route. */
export const GuestLinkRefusalPage: React.FC<{ token: string; refusal: GuestLinkRefusalError }> = ({ token, refusal }) => {
    switch (refusal.linkState) {
        case 'expired':
            return <GuestExpiredLinkPage token={token} />;
        case 'replaced':
            return <GuestReplacedLinkPage token={token} />;
        case 'legacy':
            return <GuestReplacedLinkPage token={token} legacy />;
        case 'noDelegations':
            return <GuestNoDelegationsPage />;
        default:
            return <GuestInvalidLinkPage />;
    }
};

/** Loading shell shared by the guest routes. */
export const GuestLoadingPage: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className="guest-page guest-page--loading">
        <div className="guest-page__header">
            <img src={logoWhite} alt="Molnfastigheter" className="guest-page__header-logo" />
        </div>
        <div className="guest-page__centered">{children}</div>
    </div>
);
